//
//  AdvancedScreeningResults Reducer
//  CXA Apollo
//
//  Created on 07/12/2016.
//  Copyright (c) 2016 CXA. All rights reserved.
//

// Add ALL your actions into import list
import {
  advancedScreeningResultsLoadBegin,
  advancedScreeningResultsLoadSuccess,
  advancedScreeningResultsLoadFailed,
  advancedScreeningResultDetailsLoadBegin,
  advancedScreeningResultDetailsLoadSuccess,
  advancedScreeningResultDetailsLoadFailed,
} from "../actions/advancedScreeningResultsActions";

// export const mapUserValueStatus = status => {
//   switch (status) {
//   case "very_low":
//   case "moderate":
//     return "moderate";
//   case "very_high":
//   case "high":
//     return "high";
//   case "low":
//   default:
//     return "low";
//   }
// };

export const mapStatus = status => {
  switch (status) {
    case "moderate":
    case "caution":
      return "moderate";
    case "very_high":
    case "high":
    case "High":
    case "unhealthy":
      return "high";
    case "low":
    case "Low":
    case "very_low":
    case "healthy":
    default:
      return "low";
  }
};

const initialState = {
  response: {},
  details: {},
  detailsFetching: {},
  fetching: false,
  fetched: false,
  error: null,
};

const MAX_UPPER_LIMIT = 99999; // Magic number!

export const transformValueRanges = initialRanges => {
  const maxDiff = Math.max(
    ...initialRanges.map(
      range => (range.upperLimit < MAX_UPPER_LIMIT ? range.upperLimit - range.lowerLimit : 0)
    )
  );

  const normalizedRanges = initialRanges.map(
    ({ upperLimit, lowerLimit, description, ...rest }) =>
      upperLimit < MAX_UPPER_LIMIT
        ? {
            ...rest,
            range: [lowerLimit, upperLimit],
            status: mapStatus(description),
          }
        : {
            ...rest,
            range: [lowerLimit, lowerLimit + maxDiff],
            status: mapStatus(description),
          }
  );

  return normalizedRanges;
};

const transformScreeningResultsRanges = initialRanges => {
  const parsedRanges = initialRanges.map(range => {
    const limitRegex = /([<>])([=]?)[ ]*([-]?[0-9]+[\.]?[0-9]*)/g;
    const rangeRegex = /([-]?[0-9]+[\.]?[0-9]*)[ ]*[-][ ]*([-]?[0-9]+[\.]?[0-9]*)/g;

    const { description, span, riskStatus } = range;
    const limitMatch = limitRegex.exec(description);
    const rangeMatch = rangeRegex.exec(description);

    const rangeLimit = limitMatch
      ? limitMatch[1] === "<"
        ? [null, Number(limitMatch[3])]
        : [Number(limitMatch[3]), null]
      : [Number(rangeMatch[1]), Number(rangeMatch[2])];

    return {
      ...range,
      range: rangeLimit,
      span: Number(span),
      status: mapStatus(riskStatus),
    };
  });

  const sortedRanges = parsedRanges.sort((rangeA, rangeB) => {
    const a = rangeA.range;
    const b = rangeB.range;

    if (a[0] === null || (b[0] !== null && a[0] < b[0])) {
      return -1;
    }
    if (a[1] === null || a[0] > b[0]) {
      return 1;
    }
    return 0;
  });

  const sumRangeSpan = sortedRanges.reduce((sum, range) => sum + range.span, 0);

  const { ranges } = sortedRanges.reduce(
    (res, range) => {
      const { range: initialRange, span } = range;
      const { prevUpperLimit, ranges } = res;
      const upperLimit = prevUpperLimit + span;
      return {
        prevUpperLimit: upperLimit,
        ranges: [
          ...ranges,
          {
            ...range,
            range: [(prevUpperLimit / sumRangeSpan) * 100, (upperLimit / sumRangeSpan) * 100],
          },
        ],
      };
    },
    { prevUpperLimit: 0, ranges: [] }
  );

  return ranges;
};

const transformScreeningResultsMarker = marker => {
  const {
    name,
    results: res,
    screeningResults: screeningRes,
    description: des,
    shortDescription: shortDes,
    tag,
    ...markerRest
  } = marker;

  const description = shortDes || des;
  const results = res || screeningRes;

  const [status, feedback, value, unit] = res
    ? [
        mapStatus(res.userHealthRiskDetail.riskStatus),
        res.userHealthRiskDetail.feedback,
        res.userValue,
        res.questionUnit,
      ]
    : [
        mapStatus(screeningRes.labResultStatus),
        screeningRes.healthScoreRange ? screeningRes.healthScoreRange.feedback : "",
        screeningRes.labResultValue,
        screeningRes.labResultUnit,
      ];

  const additionalProps =
    results.ranges && results.ranges.range
      ? {
          ranges: transformScreeningResultsRanges(results.ranges.range),
          rangeValue: results.ranges.pointer,
        }
      : results.valueRanges
        ? {
            remark: results.userHealthRiskDetail.remark,
            ranges: transformValueRanges(results.valueRanges),
            rangeValue: results.questionValue,
          }
        : {};

  return {
    name,
    tag,
    description,
    status,
    feedback,
    value,
    unit,
    quickTips: results.quickTips,
    ...additionalProps,
  };
};

const transformScreeningResultsSubCategory = subCategory => {
  const { biomarkers: markers, ...subCatRest } = subCategory;
  let biomarkers = [].concat(markers);

  biomarkers = biomarkers.map(transformScreeningResultsMarker);

  return { biomarkers, ...subCatRest };
};

const transformScreeningResultsCategory = category => {
  const { subCategories: subCat, subcategories: subcat, ...catRest } = category;
  let subCategories = [].concat(subCat || subcat);

  subCategories = subCategories.map(transformScreeningResultsSubCategory);

  return { subCategories, ...catRest };
};

export const transformScreeningResultsPayload = payload => {
  const { categories: cat, ...rest } = payload;
  let categories =
    cat.biomarkerCategory && cat.biomarkerCategory.length > 0 ? cat.biomarkerCategory : cat;

  if (Array.isArray(categories)) {
    categories = categories.map(transformScreeningResultsCategory);
  }

  return { categories, ...rest };
};

export default (state = initialState, action) => {
  switch (action.type) {
    case advancedScreeningResultsLoadBegin:
      return { ...state, fetching: true, details: {}, detailsFetching: {} };
    case advancedScreeningResultsLoadFailed:
      return { ...state, fetching: false, error: action.payload };
    case advancedScreeningResultsLoadSuccess:
      return {
        ...state,
        fetching: false,
        fetched: true,
        response: transformScreeningResultsPayload(action.payload),
      };
    case advancedScreeningResultDetailsLoadBegin:
      return {
        ...state,
        detailsFetching: {
          ...state.detailsFetching,
          [action.payload]: true,
        },
      };
    case advancedScreeningResultDetailsLoadFailed:
      return {
        ...state,
        error: action.payload.error,
        detailsFetching: {
          ...state.detailsFetching,
          [action.payload.marker]: false,
        },
      };
    case advancedScreeningResultDetailsLoadSuccess: {
      const marker = action.payload.biomarker;

      return {
        ...state,
        fetching: false,
        fetched: true,
        details: {
          ...state.details,
          [marker.tag]: marker,
        },
        detailsFetching: {
          ...state.detailsFetching,
          [marker.tag]: false,
        },
      };
    }
    default:
      return state; // return previous state
  }
};



// WEBPACK FOOTER //
// ./reducers/advancedScreeningResultsReducer.jsx